import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { PlusIcon, TrashIcon, CpuIcon, AlertTriangleIcon } from "lucide-react";
import Message from "../../../../i18n/Message";
import label from "../../../../i18n/label";
import { updateGateway } from "../store/gatewayActionCreators";
import { currentModelDescriptions } from "../../../../model/model-providers/modelDescriptors";
import backend from "../../../../backend/backend";

import "./GatewaySettings.scss";

const routingStrategies = [
  { value: "ROUND_ROBIN", label: "Round Robin" },
  { value: "LATENCY_BASED", label: "Latency Based" },
  { value: "LEAST_BUSY", label: "Least Busy" },
  { value: "LOWEST_COST", label: "Lowest Cost" },
];

const GatewaySettings = ({ gateway, workspaceId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // Form state
  const [models, setModels] = useState(gateway.models || []);
  const [routingStrategy, setRoutingStrategy] = useState(
    gateway.routingStrategy || "LATENCY_BASED"
  );
  const [maxTtft, setMaxTtft] = useState(gateway.maxTtft || "");
  const [maxRpm, setMaxRpm] = useState(gateway.maxRpm || "");
  const [maxTpm, setMaxTpm] = useState(gateway.maxTpm || "");
  const [retryCount, setRetryCount] = useState(gateway.retryCount || "");
  const [maxParallelRequests, setMaxParallelRequests] = useState(
    gateway.maxParallelRequests || ""
  );
  const [cooldownAllowedFails, setCooldownAllowedFails] = useState(
    gateway.cooldownAllowedFails || ""
  );
  const [cooldownTimeSeconds, setCooldownTimeSeconds] = useState(
    gateway.cooldownTimeSeconds || ""
  );
  const [latencyTimeWindowSeconds, setLatencyTimeWindowSeconds] = useState(
    gateway.latencyTimeWindowMs ? Math.round(gateway.latencyTimeWindowMs / 1000) : ""
  );
  const [latencyRequestCount, setLatencyRequestCount] = useState(
    gateway.latencyRequestCount || ""
  );
  const [cacheResponses, setCacheResponses] = useState(
    gateway.cacheResponses || false
  );

  // UI state
  const [selectedModelId, setSelectedModelId] = useState("");
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [vendorKeys, setVendorKeys] = useState([]);

  // Fetch vendor keys
  useEffect(() => {
    backend.getVendorKeys(workspaceId).then((keys) => {
      setVendorKeys(keys || []);
    });
  }, [workspaceId]);

  // Check for changes
  useEffect(() => {
    const currentModelIds = (models || []).map((m) => m.id).sort().join(",");
    const originalModelIds = (gateway.models || []).map((m) => m.id).sort().join(",");

    const originalLatencySeconds = gateway.latencyTimeWindowMs
      ? Math.round(gateway.latencyTimeWindowMs / 1000)
      : "";

    const changed =
      currentModelIds !== originalModelIds ||
      routingStrategy !== (gateway.routingStrategy || "LATENCY_BASED") ||
      (maxTtft || "") !== (gateway.maxTtft || "") ||
      (maxRpm || "") !== (gateway.maxRpm || "") ||
      (maxTpm || "") !== (gateway.maxTpm || "") ||
      (retryCount || "") !== (gateway.retryCount || "") ||
      (maxParallelRequests || "") !== (gateway.maxParallelRequests || "") ||
      (cooldownAllowedFails || "") !== (gateway.cooldownAllowedFails || "") ||
      (cooldownTimeSeconds || "") !== (gateway.cooldownTimeSeconds || "") ||
      (latencyTimeWindowSeconds || "") !== (originalLatencySeconds || "") ||
      (latencyRequestCount || "") !== (gateway.latencyRequestCount || "") ||
      cacheResponses !== (gateway.cacheResponses || false);

    setHasChanges(changed);
  }, [
    models,
    routingStrategy,
    maxTtft,
    maxRpm,
    maxTpm,
    retryCount,
    maxParallelRequests,
    cooldownAllowedFails,
    cooldownTimeSeconds,
    latencyTimeWindowSeconds,
    latencyRequestCount,
    cacheResponses,
    gateway,
  ]);

  const addModel = () => {
    if (!selectedModelId) return;
    if (models.some((m) => m.id === selectedModelId)) return;

    setModels([...models, { id: selectedModelId }]);
    setSelectedModelId("");
  };

  const removeModel = (modelId) => {
    setModels(models.filter((m) => m.id !== modelId));
  };

  const getModelName = (modelId) => {
    const model = currentModelDescriptions.find((m) => m.id === modelId);
    return model ? model.name : modelId;
  };

  const getModelVendor = (modelId) => {
    const model = currentModelDescriptions.find((m) => m.id === modelId);
    return model ? model.vendor : null;
  };

  const hasVendorKey = (vendor) => {
    if (!vendor) return true;
    const vendorKey = vendorKeys.find((k) => k.vendor === vendor);
    return vendorKey && vendorKey.content && vendorKey.content.length > 0;
  };

  const getMissingVendors = () => {
    const missingVendors = new Set();
    models.forEach((model) => {
      const vendor = getModelVendor(model.id);
      if (vendor && !hasVendorKey(vendor)) {
        missingVendors.add(vendor);
      }
    });
    return Array.from(missingVendors);
  };

  const missingVendors = getMissingVendors();

  const onSave = () => {
    setSaveInProgress(true);

    const data = {
      models,
      routingStrategy,
      maxTtft: maxTtft ? parseInt(maxTtft, 10) : null,
      maxRpm: maxRpm ? parseInt(maxRpm, 10) : null,
      maxTpm: maxTpm ? parseInt(maxTpm, 10) : null,
      retryCount: retryCount ? parseInt(retryCount, 10) : null,
      maxParallelRequests: maxParallelRequests
        ? parseInt(maxParallelRequests, 10)
        : null,
      cooldownAllowedFails: cooldownAllowedFails
        ? parseInt(cooldownAllowedFails, 10)
        : null,
      cooldownTimeSeconds: cooldownTimeSeconds
        ? parseInt(cooldownTimeSeconds, 10)
        : null,
      latencyTimeWindowMs: latencyTimeWindowSeconds
        ? parseInt(latencyTimeWindowSeconds, 10) * 1000
        : null,
      latencyRequestCount: latencyRequestCount
        ? parseInt(latencyRequestCount, 10)
        : null,
      cacheResponses,
    };

    dispatch(
      updateGateway(
        intl,
        workspaceId,
        gateway.uid,
        data,
        () => {
          setSaveInProgress(false);
        },
        () => {
          setSaveInProgress(false);
        }
      )
    );
  };

  // Filter out already selected models
  const availableModels = currentModelDescriptions.filter(
    (m) => !models.some((selected) => selected.id === m.id)
  );

  return (
    <div className="gateway_settings">
      <div className="card_header">
        <h3>
          <Message id="settings" />
        </h3>
        <button
          className="btn bg-gradient-dark btn-sm"
          onClick={onSave}
          disabled={!hasChanges || saveInProgress}
        >
          <Message id="save" />
        </button>
      </div>

      <div className="settings_body">
        {/* Models Section */}
        <div className="field_group">
          <label>
            <Message id="gateway_models" />
          </label>
          <div className="dropdown_add_row">
            <select
              className="form-control"
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
            >
              <option value="">-- Select a model --</option>
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.vendor})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline-dark btn-sm"
              onClick={addModel}
              disabled={!selectedModelId}
            >
              <PlusIcon size={16} />
              <Message id="add" />
            </button>
          </div>
          <div className="tiles_container">
            {models.map((model) => {
              const vendor = getModelVendor(model.id);
              const isMissingKey = vendor && !hasVendorKey(vendor);
              return (
                <div
                  key={model.id}
                  className={`item_tile ${isMissingKey ? "missing_key" : ""}`}
                >
                  <CpuIcon size={16} color={isMissingKey ? "#dc3545" : "#67748e"} />
                  <span>{getModelName(model.id)}</span>
                  {isMissingKey && (
                    <AlertTriangleIcon size={14} color="#dc3545" />
                  )}
                  <button
                    type="button"
                    className="btn no-btn"
                    onClick={() => removeModel(model.id)}
                  >
                    <TrashIcon size={14} color="#dc3545" />
                  </button>
                </div>
              );
            })}
            {models.length === 0 && (
              <div className="empty_models">No models selected</div>
            )}
          </div>
          {missingVendors.length > 0 && (
            <div className="missing_keys_warning">
              <AlertTriangleIcon size={16} color="#dc3545" />
              <span>
                <Message
                  id="gateway_missing_vendor_keys"
                  values={{ vendors: missingVendors.join(", ") }}
                />
              </span>
            </div>
          )}
        </div>

        {/* Routing Strategy */}
        <div className="field_group">
          <label>
            <Message id="gateway_routing_strategy" />
          </label>
          <select
            className="form-control"
            value={routingStrategy}
            onChange={(e) => setRoutingStrategy(e.target.value)}
          >
            {routingStrategies.map((strategy) => (
              <option key={strategy.value} value={strategy.value}>
                {strategy.label}
              </option>
            ))}
          </select>
        </div>

        {/* Latency-Based Routing Settings */}
        {routingStrategy === "LATENCY_BASED" && (
          <div className="field_row latency_settings">
            <div className="field_group">
              <label>
                <Message id="gateway_latency_time_window" />
              </label>
              <input
                type="number"
                className="form-control"
                value={latencyTimeWindowSeconds}
                onChange={(e) => setLatencyTimeWindowSeconds(e.target.value)}
                placeholder="300"
              />
              <span className="field_hint">seconds (default: 300 = 5 min)</span>
            </div>

            <div className="field_group">
              <label>
                <Message id="gateway_latency_request_count" />
              </label>
              <input
                type="number"
                className="form-control"
                value={latencyRequestCount}
                onChange={(e) => setLatencyRequestCount(e.target.value)}
                placeholder="20"
              />
              <span className="field_hint">requests to consider (default: 20)</span>
            </div>
          </div>
        )}

        {/* Rate Limits Section */}
        <div className="section_title">Rate Limits</div>

        <div className="field_row">
          <div className="field_group">
            <label>
              <Message id="gateway_max_rpm" />
            </label>
            <input
              type="number"
              className="form-control"
              value={maxRpm}
              onChange={(e) => setMaxRpm(e.target.value)}
              placeholder={label(intl, "gateway_max_rpm")}
            />
          </div>

          <div className="field_group">
            <label>
              <Message id="gateway_max_tpm" />
            </label>
            <input
              type="number"
              className="form-control"
              value={maxTpm}
              onChange={(e) => setMaxTpm(e.target.value)}
              placeholder={label(intl, "gateway_max_tpm")}
            />
          </div>

          <div className="field_group">
            <label>
              <Message id="gateway_max_parallel" />
            </label>
            <input
              type="number"
              className="form-control"
              value={maxParallelRequests}
              onChange={(e) => setMaxParallelRequests(e.target.value)}
              placeholder={label(intl, "gateway_max_parallel")}
            />
          </div>
        </div>

        {/* Performance Section */}
        <div className="section_title">Performance</div>

        <div className="field_row">
          <div className="field_group">
            <label>
              <Message id="gateway_max_ttft" />
            </label>
            <input
              type="number"
              className="form-control"
              value={maxTtft}
              onChange={(e) => setMaxTtft(e.target.value)}
              placeholder={label(intl, "gateway_max_ttft")}
            />
          </div>
        </div>

        {/* Retry & Cooldown Section */}
        <div className="section_title">Retry & Cooldown</div>

        <div className="field_row">
          <div className="field_group">
            <label>
              <Message id="gateway_retry_count" />
            </label>
            <input
              type="number"
              className="form-control"
              value={retryCount}
              onChange={(e) => setRetryCount(e.target.value)}
              placeholder={label(intl, "gateway_retry_count")}
            />
          </div>

          <div className="field_group">
            <label>
              <Message id="gateway_cooldown_fails" />
            </label>
            <input
              type="number"
              className="form-control"
              value={cooldownAllowedFails}
              onChange={(e) => setCooldownAllowedFails(e.target.value)}
              placeholder={label(intl, "gateway_cooldown_fails")}
            />
          </div>

          <div className="field_group">
            <label>
              <Message id="gateway_cooldown_time" />
            </label>
            <input
              type="number"
              className="form-control"
              value={cooldownTimeSeconds}
              onChange={(e) => setCooldownTimeSeconds(e.target.value)}
              placeholder={label(intl, "gateway_cooldown_time")}
            />
          </div>
        </div>

        {/* Caching Section */}
        <div className="section_title">Caching</div>

        <div className="field_group checkbox_group">
          <label>
            <input
              type="checkbox"
              checked={cacheResponses}
              onChange={(e) => setCacheResponses(e.target.checked)}
            />
            <Message id="gateway_cache_responses" />
          </label>
        </div>

        {/* Bottom Save Button */}
        <div className="field_group bottom_actions">
          <button
            className="btn bg-gradient-dark"
            onClick={onSave}
            disabled={!hasChanges || saveInProgress}
          >
            <Message id="save" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GatewaySettings;
